import { createSlice } from "@reduxjs/toolkit";

import client from "Libs/platform";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

import type { Ticket, TicketQueryParams } from "@packages/client";

export type ListTicketState = {
  isLoading: boolean;
  hasMore: boolean;
  page: number;
  tickets: Ticket[];
  error?: { error: string };
};

const initialState: ListTicketState = {
  isLoading: false,
  hasMore: false,
  page: 1,
  tickets: []
};

const defaultLoadFailureError = {
  error: "An error has occurred while loading tickets"
};

export type TicketPayload = {
  tickets?: Ticket[];
  hasMore: boolean;
  page: number;
};

const getTickets = async (filters: TicketQueryParams) => {
  const response = await client.getTickets(filters);
  const nextPage = response?._links?.next
    ? new URL(response._links.next.href).searchParams.get("page")
    : undefined;

  return {
    tickets: response.tickets ?? [],
    hasMore: !!nextPage,
    page: Number(nextPage) - 1 || 1
  };
};

// Thunks
export const load = createAppAsyncThunk(
  "tickets/list/load",
  async (filters: TicketQueryParams, { rejectWithValue }) => {
    try {
      return await getTickets(filters);
    } catch (error) {
      return rejectWithValue({
        error: (error as { message: string }).message
      });
    }
  }
);

export const loadMore = createAppAsyncThunk(
  "tickets/list/loadMore",
  async (filters: TicketQueryParams, { getState, rejectWithValue }) => {
    try {
      const { tickets } = getState();
      if (tickets.hasMore) {
        return await getTickets({
          ...filters,
          page: tickets.page + 1
        });
      }
    } catch (error) {
      return rejectWithValue({
        error: (error as { message: string }).message
      });
    }
  }
);

const listTickets = createSlice({
  name: "tickets/list",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(load.pending, state => {
        state.isLoading = true;
      })
      .addCase(load.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.hasMore = payload.hasMore;
        state.page = payload.page;
        state.tickets = payload.tickets ?? [];
      })
      .addCase(load.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload ?? defaultLoadFailureError;
      })
      .addCase(loadMore.pending, state => {
        state.isLoading = true;
      })
      .addCase(loadMore.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        if (payload) {
          state.hasMore = payload.hasMore;
          state.page = payload.page;
          state.tickets = state.tickets.concat(payload.tickets ?? []);
        }
      })
      .addCase(loadMore.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.error = payload ?? defaultLoadFailureError;
      });
  }
});

export default listTickets.reducer;
